import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  doc,
  collection,
  setDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase.js";
import { EditorForm_quil as Quill } from "../Admin/editorForm_quil.js";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useParams } from "react-router-dom";
import { GetLastDocId } from "../Api/callData.js";
import { GetModifyTarget } from "../Admin/adminWrite.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 580px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const Tab = styled.a`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Form ── */
const ContentSection = styled.section`
  padding: clamp(32px, 4vw, 48px) 0 clamp(80px, 10vw, 120px);
`;

const FormCard = styled.form`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: clamp(28px, 4vw, 48px);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 12.5px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  font-size: 15px;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: 0;
    border-color: #c7d2fe;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
  }
`;

const EditorWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  .ql-toolbar,
  .ql-container {
    border: 0;
  }
  .ql-toolbar {
    border-bottom: 1px solid #f1f5f9;
  }
  .ql-editor {
    min-height: 220px;
    font-family: inherit;
    font-size: 15px;
    line-height: 1.65;
  }
`;

const SecretBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  .label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
  }

  .opt {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #475569;
    cursor: pointer;
  }
  .opt input {
    accent-color: #6366f1;
  }
`;

const PasswordInput = styled.input`
  flex: 1;
  min-width: 140px;
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #0f172a;
  font-family: inherit;

  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: 0;
    border-color: #c7d2fe;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
  }
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const SubmitBtn = styled.button`
  padding: 12px 28px;
  border-radius: 999px;
  border: 0;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 8px 20px -10px rgba(99, 102, 241, 0.55);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px -10px rgba(99, 102, 241, 0.7);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export function InquiryWrite() {
  const location = useLocation();
  const currnetPath = location.pathname;

  const [menu, setMenu] = useState("inquiry");
  const [title, setTitle] = useState("");
  const [reg_time, setReg_time] = useState(new Date());
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState([]);
  const [secret, setSecret] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");

  const { id } = useParams();

  const handleContentChange = (value) => setContent(value);

  useEffect(() => {
    const fetchData = async () => {
      if (currnetPath.indexOf(":") !== -1 && id) {
        const textPart = id.replace(/\d+/g, "").replace(":", "");
        const trueId = id.replace(":", "");
        const targetData = await GetModifyTarget(textPart, trueId);

        setMenu(textPart);
        setTitle(targetData[0].data.title);
        setReg_time(targetData[0].data.reg_time);
        setContent(targetData[0].data.content);

        const titleEl = document.querySelector("#title");
        if (titleEl) titleEl.value = targetData[0].data.title;
        const editor = document.querySelector(".ql-editor.ql-blank");
        if (editor) editor.innerHTML = targetData[0].data.content;
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const images = doc.querySelectorAll("img");
    if (Array.from(images).length !== 0) {
      const extractUrl = Array.from(images).map((img) => img.src);
      setImageUrl(extractUrl);
    }
  }, [content]);

  const uploadImage = async (file) => {
    const dateFolder = new Date().toISOString().split("T")[0];
    const fileRef = ref(storage, `images/${dateFolder}/${file.name}`);
    await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(fileRef);
    return downloadURL;
  };

  const dataURLtoFile = (dataurl) => {
    if (!dataurl || !dataurl.startsWith("data:")) return null;
    const uniqueFileName = uuidv4() + ".jpg";
    const arr = dataurl.split(",");
    if (arr.length < 2) return null;
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], uniqueFileName, { type: mime });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitted(true);

    if (!title || title === "") {
      alert("Please enter a title");
      document.querySelector("#title").focus();
      setIsSubmitted(false);
      return false;
    }
    if (!content || content === "") {
      alert("Please enter content");
      setIsSubmitted(false);
      return false;
    }
    if (!name || name === "") {
      alert("Please enter your name");
      document.querySelector("#userName").focus();
      setIsSubmitted(false);
      return false;
    }
    if (secret && pw === "") {
      alert("Please enter your password");
      document.querySelector("#secretPw").focus();
      setIsSubmitted(false);
      return false;
    }

    let thumnail_src = [];
    let uploadedUrls = [];

    if (id) {
      thumnail_src = imageUrl;
      const newImages = imageUrl.filter((url) => url.startsWith("data:"));
      if (newImages.length > 0) {
        uploadedUrls = await Promise.all(
          newImages.map(async (uploadedUrl) => {
            const file = dataURLtoFile(uploadedUrl);
            return await uploadImage(file);
          })
        );
        thumnail_src = uploadedUrls;
      }
    } else {
      const dataImages = imageUrl.filter((url) => url.startsWith("data:"));
      uploadedUrls = await Promise.all(
        dataImages.map(async (uploadedUrl) => {
          const file = dataURLtoFile(uploadedUrl);
          return await uploadImage(file);
        })
      );
      thumnail_src = uploadedUrls;
    }

    let imageIndex = 0;
    const changeContent = content.replace(
      /<img[^>]*src="([^"]*base64[^"]*)"/g,
      (match) => {
        if (imageIndex < uploadedUrls.length) {
          const newSrc = uploadedUrls[imageIndex++];
          return `<img src="${newSrc}" /`;
        }
        return match;
      }
    );

    let content_txt = changeContent
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&[a-zA-Z]{1,6};/g, "")
      .replace(/&#[0-9]{1,6};/g, "");

    const newItem = {
      title,
      content: changeContent,
      content_txt,
      userName: name,
      reg_time,
      secret: pw,
      secret_check: secret,
    };

    setIsSubmitted(false);

    if (menu && menu.trim() !== "") {
      try {
        const lastDocId = await GetLastDocId(menu);
        let uniqueId;
        if (lastDocId) {
          const match = lastDocId.match(/([a-zA-Z]+)(\d+)/);
          if (match) {
            uniqueId = match[1] + (parseInt(match[2], 10) + 1);
          }
        } else {
          uniqueId = menu + "1";
        }
        if (id) {
          uniqueId = id.replace(":", "");
        }

        await setDoc(doc(collection(db, menu), uniqueId), newItem);
        alert("Post has been registered.");
        window.history.back();
      } catch (error) {
        console.error("등록 오류", error);
      }
    } else {
      alert("Menu values have not been set. Please select a menu.");
    }
  };

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Support</Eyebrow>
          <HeroTitle>{id ? "Edit inquiry" : "Write inquiry"}</HeroTitle>
          <HeroLead>
            Have a question? Send it to our team and we&apos;ll get back to you.
          </HeroLead>
          <Tabs>
            <Tab href="/support/faq">FAQ</Tab>
            <Tab href="/support/inquiry" $active>
              Inquiry
            </Tab>
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <FormCard id="boardForm" onSubmit={handleSubmit}>
            <Field>
              <label htmlFor="title">Title</label>
              <TextInput
                id="title"
                name="title"
                type="text"
                placeholder="What is your question about?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>

            <Field>
              <label>Message</label>
              <EditorWrap>
                <Quill onChange={handleContentChange} />
              </EditorWrap>
            </Field>

            <Field>
              <label htmlFor="userName">Your name</label>
              <TextInput
                id="userName"
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>

            <Field>
              <label>Privacy</label>
              <SecretBox>
                <span className="label">Visibility</span>
                <label className="opt" htmlFor="visPublic">
                  <input
                    id="visPublic"
                    type="radio"
                    name="secret"
                    onChange={() => setSecret(false)}
                    checked={secret === false}
                  />
                  Public
                </label>
                <label className="opt" htmlFor="visPrivate">
                  <input
                    id="visPrivate"
                    type="radio"
                    name="secret"
                    onChange={() => setSecret(true)}
                    checked={secret === true}
                  />
                  Private
                </label>
                <PasswordInput
                  id="secretPw"
                  type="password"
                  placeholder="Set a password"
                  disabled={!secret}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                />
              </SecretBox>
            </Field>

            <Actions>
              <SubmitBtn type="submit" disabled={isSubmitted}>
                {id ? "Save changes" : "Submit inquiry"}
              </SubmitBtn>
            </Actions>
          </FormCard>
        </Container>
      </ContentSection>
    </Page>
  );
}
